/**
 * ImportTool.tsx
 * CSV and Excel import tool for migrating reviews
 * Path: components/admin-tabs/reviews/ImportTool.tsx
 */

import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx'; // Requires 'npm install xlsx'
import { createClient } from '@supabase/supabase-js'; // Fix: Import Supabase directly
import { api } from '../../../services/api';
import { UserProfile } from '../../../types';
import { Upload, FileText, AlertCircle, CheckCircle, Download, RefreshCw, XCircle } from 'lucide-react';

// Initialize Supabase Client directly to fix the "undefined" error
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

interface ImportToolProps {
  user: UserProfile;
}

export const ImportTool: React.FC<ImportToolProps> = ({ user }) => {
  const [source, setSource] = useState<'judgeme' | 'loox' | 'custom'>('judgeme');
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // New state for background media sync
  const [migrationStatus, setMigrationStatus] = useState<any>(null);

  // Poll for migration status on mount and when active
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        // Fix: Use 'supabase' directly instead of 'api.supabase'
        const { data } = await supabase
          .from('review_migration_job')
          .select('*')
          .eq('id', 1)
          .single();
        
        if (data) {
          setMigrationStatus(data);
        }
      } catch (e) {
        console.error("Error checking migration status", e);
      }
    };

    // Initial check
    fetchStatus();

    // Poll every 2 seconds if active
    let interval: any;
    if (migrationStatus?.is_active) {
      interval = setInterval(fetchStatus, 2000);
    }
    
    return () => clearInterval(interval);
  }, [migrationStatus?.is_active]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Allow CSV and Excel files
      const isExcel = selectedFile.name.match(/\.(csv|xlsx|xls)$/i);
      
      if (isExcel) {
        setFile(selectedFile);
        setError(null);
        setResult(null);
      } else {
        setError('Please select a valid CSV or Excel (.xlsx) file');
        setFile(null);
      }
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    // Block import if a sync job is currently running
    if (migrationStatus?.is_active) {
      setError("A media sync job is currently running. Please wait for it to finish or stop it before importing new reviews.");
      return;
    }

    setImporting(true);
    setError(null);
    setResult(null);
    setProgress(0);

    try {
      setProgress(5);
      
      // 1. Read file as binary buffer (Preserves Emojis)
      const data = await file.arrayBuffer();
      
      // 2. Parse the workbook using SheetJS
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      
      // 3. Convert to JSON directly
      const rawData = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      setProgress(20);

      // 4. Clean keys
      const csvData = rawData.map((row: any) => {
        const cleanRow: any = {};
        Object.keys(row).forEach(key => {
          const cleanKey = key.trim().replace(/^"|"$/g, '');
          cleanRow[cleanKey] = row[key];
        });
        return cleanRow;
      });

      // 5. Filter valid rows
      const validRows = csvData.filter((row: any) => {
          if (source === 'judgeme' || source === 'loox') return true; 
          return row.product_id && row.rating;
      });

      // --- START BATCH PROCESSING (Fixes 546 Timeout) ---
      const BATCH_SIZE = 100;
      const totalReviews = validRows.length;
      let processedCount = 0;
      
      // Store combined results here
      const finalResult = {
        total_imported: totalReviews,
        successful: 0,
        skipped: 0,
        failed: 0,
        errors: [] as string[]
      };

      console.log(`Starting batch import for ${totalReviews} reviews...`);

      // Loop through data in chunks of 100
      for (let i = 0; i < totalReviews; i += BATCH_SIZE) {
        const batch = validRows.slice(i, i + BATCH_SIZE);
        
        try {
          // Send one batch
          const batchResult = await api.importReviewsFromCSV(user.id, batch, source);
          
          // Add this batch's results to the total
          finalResult.successful += batchResult.successful || 0;
          finalResult.skipped += batchResult.skipped || 0;
          finalResult.failed += batchResult.failed || 0;
          
          if (batchResult.errors && Array.isArray(batchResult.errors)) {
            finalResult.errors = [...finalResult.errors, ...batchResult.errors];
          }
        } catch (batchErr: any) {
          console.error(`Batch ${i} failed:`, batchErr);
          finalResult.failed += batch.length;
          finalResult.errors.push(`Batch starting at row ${i} failed: ${batchErr.message}`);
        }

        // Update progress bar (mapped from 30% to 100%)
        processedCount += batch.length;
        const percentage = Math.round((processedCount / totalReviews) * 100);
        setProgress(30 + (percentage * 0.7));
      }
      // --- END BATCH PROCESSING ---

      setResult(finalResult);
      setProgress(100);
      
      if (finalResult.successful > 0) {
        // --- AUTO-START MEDIA SYNC ---
        try {
            // 1. Initialize the Migration Job in DB using direct supabase client
            // Fix: Replaced 'api.supabase' with 'supabase'
            await supabase.from('review_migration_job').upsert({
                id: 1,
                is_active: true,
                status: 'running',
                total_reviews: finalResult.successful,
                processed_count: 0
            });
            
            // 2. Trigger the Edge Function (Fire and Forget)
            const { data: sessionData } = await supabase.auth.getSession();
            const token = sessionData.session?.access_token;
            
            fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-media`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            // 3. Update Local State to show UI immediately
            setMigrationStatus({ 
                is_active: true, 
                status: 'running', 
                total_reviews: finalResult.successful, 
                processed_count: 0 
            });

            alert(`✅ Import Complete!\n\n${finalResult.successful} reviews imported.\n\nBackground media sync has started automatically. You can close this tab.`);
        } catch (syncErr) {
            console.error("Failed to auto-start sync", syncErr);
            alert("Import success, but failed to start auto-sync. Please check console.");
        }
      } else if (finalResult.skipped > 0 || finalResult.failed > 0) {
         const message = `⚠️ Import Finished\n\n` +
          `${finalResult.successful} successful\n` +
          `${finalResult.skipped} duplicates skipped\n` +
          `${finalResult.failed} failed`;
         alert(message);
      }

    } catch (err: any) {
      console.error('Import error:', err);
      setError(err.message || 'Failed to import reviews');
      setProgress(0);
    } finally {
      setImporting(false);
    }
  };

  const handleStopSync = async () => {
    if (!confirm("Are you sure you want to stop the background media sync?")) return;
    
    try {
        // Fix: Use 'supabase' directly
        await supabase.from('review_migration_job')
            .update({ is_active: false, status: 'stopped_by_user' })
            .eq('id', 1);
            
        setMigrationStatus((prev: any) => ({ ...prev, is_active: false, status: 'stopped_by_user' }));
    } catch (err) {
        console.error("Failed to stop sync", err);
        alert("Failed to stop sync.");
    }
  };

  const downloadTemplate = (type: 'judgeme' | 'loox' | 'custom') => {
    let csvContent = '';
    
    if (type === 'judgeme') {
      csvContent = 'id,title,body,rating,reviewer_name,reviewer_email,product_id,created_at,verified,pictures\n';
      csvContent += 'example123,Great Product,I love this product! ❤️,5,John Doe,john@example.com,prod_123,2024-01-01,true,https://example.com/image.jpg\n';
    } else if (type === 'loox') {
      csvContent = 'Review ID,Product ID,Customer Name,Customer Email,Rating,Review Text,Date,Verified Purchase,Image URLs\n';
      csvContent += '123,prod_456,Jane Smith,jane@example.com,5,Amazing quality 🔥,2024-01-01,Yes,https://example.com/image.jpg\n';
    } else {
      csvContent = 'product_id,rating,content,reviewer_name,reviewer_email,created_at,verified_purchase,media_urls\n';
      csvContent += 'prod_789,5,Excellent product! ⭐,Bob Johnson,bob@example.com,2024-01-01,true,https://example.com/image.jpg\n';
    }

    const blob = new Blob(["\uFEFF" + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}_template.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      
      {/* --- MEDIA SYNC STATUS CARD --- */}
      {migrationStatus && (migrationStatus.is_active || migrationStatus.status === 'running') && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 shadow-sm animate-pulse-slow">
            <div className="flex justify-between items-start mb-3">
                <div>
                    <h4 className="flex items-center gap-2 font-bold text-blue-900 text-lg">
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        Media Sync in Progress
                    </h4>
                    <p className="text-sm text-blue-800 mt-1">
                        Moving images/videos from external URLs to your storage. 
                        <strong> It is safe to close this tab.</strong>
                    </p>
                </div>
                <button
                    onClick={handleStopSync}
                    className="flex items-center gap-2 bg-white text-red-600 border border-red-200 px-3 py-1.5 rounded hover:bg-red-50 text-sm font-medium transition-colors"
                >
                    <XCircle className="w-4 h-4" />
                    Stop Sync
                </button>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-blue-200 rounded-full h-4 overflow-hidden mb-2">
                <div 
                    className="bg-blue-600 h-full transition-all duration-500 ease-out"
                    style={{ width: `${Math.min(100, ((migrationStatus.processed_count || 0) / (migrationStatus.total_reviews || 1)) * 100)}%` }}
                />
            </div>
            <div className="flex justify-between text-xs font-mono text-blue-800">
                <span>Processed: {migrationStatus.processed_count || 0} / {migrationStatus.total_reviews || '?'}</span>
                <span>{Math.round(((migrationStatus.processed_count || 0) / (migrationStatus.total_reviews || 1)) * 100)}%</span>
            </div>
        </div>
      )}

      {/* --- STOPPED STATUS (Optional visual feedback) --- */}
      {migrationStatus && !migrationStatus.is_active && migrationStatus.status === 'stopped_by_user' && (
         <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <div className="text-red-900 text-sm">
                <strong>Sync Stopped.</strong> You can restart it by importing again or contacting support.
            </div>
         </div>
      )}

      {/* Source Selection */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Import Source</h3>
        <div className="grid grid-cols-3 gap-4">
          {[
            { id: 'judgeme', label: 'Judge.me', desc: 'Import from Judge.me export' },
            { id: 'loox', label: 'Loox', desc: 'Import from Loox export' },
            { id: 'custom', label: 'Custom CSV/Excel', desc: 'Use custom format' },
          ].map((option) => (
            <button
              key={option.id}
              onClick={() => setSource(option.id as any)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                source === option.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="font-medium text-gray-900">{option.label}</div>
              <div className="text-sm text-gray-600 mt-1">{option.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* CSV Template Download */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h4 className="font-semibold text-blue-900 mb-1">Need a template?</h4>
            <p className="text-sm text-blue-800 mb-3">
              Download a template with the correct format for {source === 'judgeme' ? 'Judge.me' : source === 'loox' ? 'Loox' : 'custom'} imports.
            </p>
            <button
              onClick={() => downloadTemplate(source)}
              className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              <Download className="w-4 h-4" />
              Download {source.charAt(0).toUpperCase() + source.slice(1)} Template
            </button>
          </div>
        </div>
      </div>

      {/* File Upload */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Upload File</h3>
        
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          
          {!file ? (
            <>
              <label className="cursor-pointer">
                <span className="text-blue-600 hover:text-blue-700 font-medium">
                  Click to upload
                </span>
                <span className="text-gray-600"> or drag and drop</span>
                <input
                  type="file"
                  accept=".csv, .xlsx, .xls"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
              <p className="text-sm text-gray-500 mt-2">CSV or Excel (.xlsx) files</p>
            </>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-2 text-gray-900">
                <FileText className="w-5 h-5" />
                <span className="font-medium">{file.name}</span>
              </div>
              <button
                onClick={() => setFile(null)}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Remove file
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Import Button */}
      {file && (
        <div className="space-y-4">
          <div className="flex gap-3">
            <button
              onClick={handleImport}
              disabled={importing || (migrationStatus && migrationStatus.is_active)}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="w-5 h-5" />
              {importing ? 'Importing...' : 'Import Reviews'}
            </button>
          </div>
          
          {/* Progress Bar */}
          {importing && progress > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-900">Importing...</span>
                <span className="text-sm text-blue-600">{progress}%</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-red-900 mb-1">Import Failed</h4>
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Success Result */}
      {result && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-green-900 mb-2">Import Completed</h4>
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-green-600">Total Rows</div>
                  <div className="text-2xl font-bold text-green-900">{result.total_imported}</div>
                </div>
                <div>
                  <div className="text-green-600">Imported</div>
                  <div className="text-2xl font-bold text-green-900">{result.successful}</div>
                </div>
                <div>
                  <div className="text-yellow-600">Skipped</div>
                  <div className="text-2xl font-bold text-yellow-900">{result.skipped || 0}</div>
                </div>
                <div>
                  <div className="text-red-600">Failed</div>
                  <div className="text-2xl font-bold text-red-900">{result.failed}</div>
                </div>
              </div>
              
              {result.skipped > 0 && (
                <p className="text-sm text-yellow-800 mt-3 bg-yellow-50 p-2 rounded">
                  ⚠️ {result.skipped} duplicate review{result.skipped !== 1 ? 's' : ''} skipped
                </p>
              )}
              
              {result.failed > 0 && (
                <p className="text-sm text-red-800 mt-3 bg-red-50 p-2 rounded">
                  ❌ {result.failed} review{result.failed !== 1 ? 's' : ''} failed to import
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Import Instructions */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3">Import Instructions</h4>
        <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
          <li>Export your reviews from {source === 'judgeme' ? 'Judge.me' : source === 'loox' ? 'Loox' : 'your current platform'} as CSV or Excel.</li>
          <li><strong>Note:</strong> Excel (.xlsx) files are recommended for best Emoji support.</li>
          <li>Make sure your columns match the template format.</li>
          <li>Upload the file using the form above.</li>
          <li>Imported reviews will be set to "pending" status.</li>
        </ol>
      </div>

      {/* Field Mapping Guide */}
      <div>
        <h4 className="font-semibold text-gray-900 mb-3">Required CSV/Excel Fields</h4>
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-900">Field Name</th>
                <th className="px-4 py-3 text-left font-medium text-gray-900">Description</th>
                <th className="px-4 py-3 text-left font-medium text-gray-900">Required</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-3 font-mono text-blue-600">product_id</td>
                <td className="px-4 py-3 text-gray-700">Product identifier (SKU or ID)</td>
                <td className="px-4 py-3 text-green-600">Yes</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono text-blue-600">rating</td>
                <td className="px-4 py-3 text-gray-700">Rating (1-5)</td>
                <td className="px-4 py-3 text-green-600">Yes</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono text-blue-600">content</td>
                <td className="px-4 py-3 text-gray-700">Review text</td>
                <td className="px-4 py-3 text-green-600">Yes</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono text-blue-600">reviewer_name</td>
                <td className="px-4 py-3 text-gray-700">Customer name</td>
                <td className="px-4 py-3 text-green-600">Yes</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono text-blue-600">reviewer_email</td>
                <td className="px-4 py-3 text-gray-700">Customer email</td>
                <td className="px-4 py-3 text-gray-500">Optional</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono text-blue-600">created_at</td>
                <td className="px-4 py-3 text-gray-700">Review date (YYYY-MM-DD)</td>
                <td className="px-4 py-3 text-gray-500">Optional</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-mono text-blue-600">media_urls</td>
                <td className="px-4 py-3 text-gray-700">Image URLs (comma-separated)</td>
                <td className="px-4 py-3 text-gray-500">Optional</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
